import { ref, type Ref } from 'vue'

/**
 * A template ref for a component whose logic lives in `setup()` but whose markup is a `render()`.
 *
 * A string ref -- `ref: 'qInput'` -- does not populate `$refs` for that shape under Vue 3, so every
 * `this.$refs.x` in the bundle silently read undefined: BsChipBox never attached its paste handler,
 * BsInput could not focus, BsTabStepper could not reach its form. Nothing failed loudly.
 *
 * Returns the ref itself plus the setter to hand `h()`. The setter is what the render passes, because
 * `this.someRef` auto-unwraps to the VALUE (null at render time) and the render cannot see setup scope.
 * Both go in setup's return so the options-API methods can read `this.<name>` normally.
 *
 *   const { ref: qInput, set: setQInput } = useTemplateRef<QInput>()
 *   // setup returns { qInput, setQInput }
 *   // render:  h(QInput, { ref: this.setQInput })
 *   // methods: (this.qInput as QInput).focus()
 */
export function useTemplateRef<T> (): { ref: Ref<T | null>, set: (el: unknown) => void } {
  const target = ref<T | null>(null) as Ref<T | null>

  return {
    ref: target,
    set: (el: unknown) => { target.value = (el ?? null) as T | null }
  }
}
